import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import {
  CustomPageArbitraryFilterFieldsContext,
  CustomPageBrandContext,
  CustomPageDispatchActionContext,
  CustomPageProxiesClientContext,
  CustomPageSetArbitraryFilterFieldsContext,
  CustomPageSubjectContext,
  CustomPageUserContext,
  FetchUserTerritoriesContext,
  type CustomPageArbitraryFilterFields,
  type CustomPageBrand,
  type CustomPageDispatchAction,
  type CustomPageRendererContextValue,
  type CustomPageSubject,
  type CustomPageUser,
  type UserTerritory,
} from './default.js';

type CustomPageRendererContextProviderProps = {
  value: CustomPageRendererContextValue;
  fetchUserTerritories: () => Promise<UserTerritory[]>;
  children: ReactNode;
};

export function CustomPageRendererContextProvider({
  value,
  fetchUserTerritories,
  children,
}: CustomPageRendererContextProviderProps): ReactElement {
  return (
    <CustomPageDispatchActionContext.Provider value={value.dispatchAction}>
      <CustomPageProxiesClientContext.Provider value={value.proxiesClient}>
        <CustomPageSubjectContext.Provider value={value.subject}>
          <CustomPageUserContext.Provider value={value.user}>
            <CustomPageBrandContext.Provider value={value.brand}>
              <CustomPageArbitraryFilterFieldsContext.Provider value={value.arbitraryFilterFields}>
                <CustomPageSetArbitraryFilterFieldsContext.Provider value={value.setArbitraryFilterFields}>
                  <FetchUserTerritoriesContext.Provider value={fetchUserTerritories}>
                    {children}
                  </FetchUserTerritoriesContext.Provider>
                </CustomPageSetArbitraryFilterFieldsContext.Provider>
              </CustomPageArbitraryFilterFieldsContext.Provider>
            </CustomPageBrandContext.Provider>
          </CustomPageUserContext.Provider>
        </CustomPageSubjectContext.Provider>
      </CustomPageProxiesClientContext.Provider>
    </CustomPageDispatchActionContext.Provider>
  );
}

type DummyCustomPageRendererContextProviderProps = {
  children: ReactNode;
};

const DUMMY_CUSTOM_PAGE_USER: CustomPageUser = {
  identifier: 'dummy-user',
  name: 'Preview User',
  email: 'preview.user@example.com',
  metadata: {},
  brandIndications: [],
};

const DUMMY_CUSTOM_PAGE_BRAND: CustomPageBrand = {
  identifier: 'dummy-brand',
  name: 'Preview Brand',
};

const DUMMY_CUSTOM_PAGE_SUBJECT: CustomPageSubject = {
  id: 'dummy-subject',
  name: 'Preview Subject',
  kind: 'demo',
};

export function DummyCustomPageRendererContextProvider({
  children,
}: DummyCustomPageRendererContextProviderProps): ReactElement {
  const [subject, setSubject] = useState<CustomPageSubject>(DUMMY_CUSTOM_PAGE_SUBJECT);
  const [arbitraryFilterFields, setArbitraryFilterFields] = useState<CustomPageArbitraryFilterFields>({});

  const dispatchAction = useCallback<CustomPageDispatchAction>((action) => {
    switch (action.type) {
      case 'update-subject':
      case 'open-new-chat-for-subject':
        setSubject(action.payload.subject);
        break;
      case 'noop':
      case 'update-input':
      case 'submit-query':
        break;
      default:
        break;
    }
  }, []);

  return (
    <CustomPageRendererContextProvider
      value={{
        proxiesClient: null,
        subject,
        user: DUMMY_CUSTOM_PAGE_USER,
        brand: DUMMY_CUSTOM_PAGE_BRAND,
        arbitraryFilterFields,
        setArbitraryFilterFields,
        dispatchAction,
      }}
      fetchUserTerritories={() => Promise.resolve([])}
    >
      {children}
    </CustomPageRendererContextProvider>
  );
}
